import loadable from '@loadable/component';
import type { ConfigType } from '@plone/registry';
import { defineMessages } from 'react-intl';
import {
  CarouselTemplate,
  DefaultSlideAppearance,
  // CarouselReadMoreButton,
} from '@redturtle/qubicaamf-theme/components/Blocks/Listing/Carousel';
import CarouselTemplateSkeleton from '@redturtle/qubicaamf-theme/components/Blocks/Listing/TemplatesSkeletons/CarouselTemplateSkeleton';

// extenders
import { showCarouselButtons } from '@redturtle/qubicaamf-theme/components/Blocks/extenders/carousel/showCarouselButtons';
import { showSlideIcon } from '@redturtle/qubicaamf-theme/components/Blocks/extenders/carousel/showSlideIcon';
import { addCtaFieldset } from '@redturtle/qubicaamf-theme/components/Blocks/extenders/cta';

const applyCarouselConfig = (config: ConfigType) => {
  config.settings.loadables = {
    ...((config.settings.loadables as any) ?? {}),
    swiper: loadable.lib(() => import('swiper/react')),
    swiperModules: loadable.lib(() => import('swiper/modules')),
  };

  config.settings['volto-carousel'] = {
    ...((config.settings['volto-carousel'] as any) ?? {}),
    slide_appearances: {
      ...((config.settings['volto-carousel'] as any)?.slide_appearances ?? {}),
      default: {
        name: 'Default',
        component: DefaultSlideAppearance,
      },
    },
    slots: {
      // bottom: { component: CarouselReadMoreButton },
    },
  };

  config.blocks.blocksConfig.listing = {
    ...config.blocks.blocksConfig.listing,
    variations: [
      ...config.blocks.blocksConfig.listing.variations,
      {
        id: 'carousel',
        isDefault: false,
        title: 'Carousel',
        template: CarouselTemplate,
        // @ts-ignore
        skeleton: CarouselTemplateSkeleton,
        schemaEnhancer: ({ schema, formData, intl }) => {
          const fieldset = 'default';
          const beforeField = 'querystring';

          let fieldsetIndex = schema.fieldsets.findIndex(
            (x) => x.id === fieldset,
          );
          let position =
            schema.fieldsets[fieldsetIndex]?.fields.indexOf(beforeField);

          //head_position
          schema.fieldsets[fieldsetIndex]?.fields?.splice(
            position,
            0,
            'head_position',
          );
          schema.properties['head_position'] = {
            title: intl.formatMessage(messages.head_position),
            description: null,
            choices: [
              ['center', intl.formatMessage(messages.head_position_center)],
              ['left', intl.formatMessage(messages.head_position_left)],
            ],
            default: 'center',
          };
          position++;

          // block description
          schema.fieldsets[fieldsetIndex]?.fields?.splice(
            position,
            0,
            'block_description',
          );
          schema.properties['block_description'] = {
            title: intl.formatMessage(messages.block_description),
            widget: 'textarea',
          };
          position++;

          //full-width
          // // eslint-disable-next-line no-unused-expressions
          schema.fieldsets[fieldsetIndex]?.fields?.splice(
            position,
            0,
            'full_width',
          );
          schema.properties['full_width'] = {
            title: intl.formatMessage(messages.full_width),
            description: null,
            type: 'boolean',
          };
          position++;

          //display_dots
          schema.fieldsets[fieldsetIndex]?.fields?.splice(
            position,
            0,
            'display_dots',
          );
          schema.properties['display_dots'] = {
            title: intl.formatMessage(messages.display_dots),
            description: null,
            type: 'boolean',
            default: false,
          };
          position++;

          //autoplay
          schema.fieldsets[fieldsetIndex]?.fields?.splice(
            position,
            0,
            'autoplay',
          );
          schema.properties['autoplay'] = {
            title: intl.formatMessage(messages.autoplay),
            description: intl.formatMessage(messages.autoplay_description),
            type: 'boolean',
          };
          position++;

          //autoplay speed
          schema.fieldsets[fieldsetIndex]?.fields?.splice(
            position,
            0,
            'autoplay_speed',
          );
          schema.properties['autoplay_speed'] = {
            title: intl.formatMessage(messages.autoplay_speed),
            description: intl.formatMessage(
              messages.autoplay_speed_description,
            ),
            type: 'number',
            default: 2,
          };
          position++;

          //slides to show
          schema.fieldsets[fieldsetIndex]?.fields?.splice(
            position,
            0,
            'slides_to_show',
          );
          schema.properties['slides_to_show'] = {
            title: intl.formatMessage(messages.slides_to_show),
            type: 'number',
            default: 5,
          };

          position++;

          schema.fieldsets[fieldsetIndex]?.fields?.splice(
            position,
            0,
            'space_between',
          );
          schema.properties['space_between'] = {
            title: intl.formatMessage(messages.space_between),
            type: 'number',
            default: 100,
          };

          let choices = [];
          Object.keys(
            (config.settings['volto-carousel'] as any).slide_appearances,
          ).forEach((a) => {
            choices.push([
              a,
              (config.settings['volto-carousel'] as any).slide_appearances[a]
                .name,
            ]);
          });

          if (choices.length > 1) {
            schema.fieldsets[fieldsetIndex]?.fields?.splice(
              position,
              0,
              'slide_appearance',
            );
            schema.properties['slide_appearance'] = {
              title: intl.formatMessage(messages.slide_appearance),
              type: 'text',
              choices: choices,
              default: 'default',
            };

            position++;
          }

          schema.fieldsets[fieldsetIndex]?.fields?.splice(
            position,
            0,
            'item_show_link_more',
          );
          schema.properties['item_show_link_more'] = {
            title: intl.formatMessage(messages.item_show_detail),
            type: 'boolean',
            default: false,
          };
          position++;

          //item_detail_label
          // // eslint-disable-next-line no-unused-expressions
          schema.fieldsets[fieldsetIndex]?.fields?.splice(
            position,
            0,
            'item_detail_label',
          );
          schema.properties['item_detail_label'] = {
            title: intl.formatMessage(messages.item_detail_label),
            description: null,
            default: intl.formatMessage(messages.view),
          };
          position++;

          showCarouselButtons({
            schema,
            intl,
            formData,
            fieldsetName: 'default',
            beforeField: 'display_dots',
          });

          showSlideIcon({
            schema,
            intl,
            formData,
            fieldsetName: 'default',
            beforeField: 'display_dots',
          });

          return schema;
        },
      },
    ],
  };

  return config;
};

export default applyCarouselConfig;

const messages = defineMessages({
  head_position: {
    id: 'head_position',
    defaultMessage: 'Headline position',
  },
  block_description: {
    id: 'block_description',
    defaultMessage: 'Block description',
  },
  head_position_center: {
    id: 'carousel_head_position_center',
    defaultMessage: 'Center',
  },
  head_position_left: {
    id: 'carousel_head_position_left',
    defaultMessage: 'Left',
  },
  full_width: {
    id: 'full_width',
    defaultMessage: 'Full width',
  },
  display_dots: {
    id: 'display_dots',
    defaultMessage: 'Show pagination dots',
  },
  autoplay: {
    id: 'autoplay',
    defaultMessage: 'Autoplay',
  },
  autoplay_description: {
    id: 'autoplay_description',
    defaultMessage:
      'Note: for accessibility reasons, autoplay should not be enabled by default for a carousel.',
  },
  autoplay_speed: {
    id: 'autoplay_speed',
    defaultMessage: 'Autoplay speed',
  },
  autoplay_speed_description: {
    id: 'autoplay_speed_description',
    defaultMessage: 'Autoplay speed expressed in seconds',
  },
  slides_to_show: {
    id: 'slides_to_show',
    defaultMessage: 'Number of slides to show',
  },
  space_between: {
    id: 'space_between',
    defaultMessage: 'Space inbetween slides (in px)',
  },
  slide_appearance: {
    id: 'slide_appearance',
    defaultMessage: 'Slide appearance',
  },
  view: {
    id: 'carousel_item_detail_label_default',
    defaultMessage: 'Read more',
  },
  item_show_detail: {
    id: 'item_show_detail',
    defaultMessage: 'Show link more',
  },
  item_detail_label: {
    id: 'carousel_item_detail_label',
    defaultMessage: 'Label for link details',
  },
});
